
import router from '@/router';
import store from '@/store';
import requestAPI from '@/logic/requestBeApi';
import genderList from '@/resources/genderList';
import { authLogOut, refreshTokenHandler } from '@/logic/auth';
import EazyHeading from '@/components/EazyHeading.vue';
import UserProfileCompletionSteps from '@/components/UserProfileCompletionSteps.vue';
import BlueCheckbox from '@/components/BlueCheckbox.vue';
import passwordStrength from '@/logic/passwordStrength';
import helpToDeleteAccount from '@/logic/helpers/helpToDeleteAccount';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput.vue';
import CountryInput from '@/components/Inputs/CountryInput.vue';
import formValidators from '@/logic/helpers/forms/validators';
import inputValidators from '@/logic/helpers/inputs/validators';
import VatNumberInput from '@/components/Inputs/VatNumberInput.vue';
import newsletter from '@/logic/services/newsletter';

export default {
  name: 'ClientProfile',

  components: {
    EazyHeading,
    UserProfileCompletionSteps,
    BlueCheckbox,
    PhoneNumberInput,
    CountryInput,
    VatNumberInput,
  },

  data(): Record<string, unknown> {
    return {
      router,
      onboardedFully: false,
      clientProfilePersonalDetails: {
        email: '',
        password: '',
        firstName: '',
        initials: '',
        surname: '',
        gender: undefined,
        position: '',
      },
      personalDetailsValidationRules: {
        email: [{ validator: inputValidators.email, trigger: 'change' }],
        firstName: [{ validator: inputValidators.primitive, trigger: 'change' }],
        surname: [{ validator: inputValidators.primitive, trigger: 'change' }],
        gender: [{ validator: inputValidators.gender, trigger: 'change' }],
        position: [{ validator: inputValidators.primitive, trigger: 'change' }],
      },
      clientProfileCompanyDetails: {
        companyEmail: '',
        companyName: '',
        street: '',
        houseNumber: '',
        apartmentNumber: '',
        city: '',
        postalCode: '',
        country: '',
        vatNumber: '',
        registrationNumber: '',
        phone: '',
        PONumbers: false,
      },
      companyDetailsValidationRules: {
        companyEmail: [{ validator: inputValidators.email, trigger: 'change' }],
        companyName: [{ validator: inputValidators.primitive, trigger: 'change' }],
        street: [{ validator: inputValidators.primitive, trigger: 'change' }],
        houseNumber: [{ validator: inputValidators.primitive, trigger: 'change' }],
        city: [{ validator: inputValidators.primitive, trigger: 'change' }],
        postalCode: [{ validator: inputValidators.primitive, trigger: 'change' }],
        country: [{ validator: inputValidators.primitive, trigger: 'change' }],
        vatNumber: [{ validator: inputValidators.vatNumber, trigger: 'change' }],
      },
      genderList,
      originalEmail: '',
      passwordInputIcon: '',
      companyId: '',
      weakPasswordError: '',
      processingUserDataRequest: false,
      userSubmittedOnboardingBasics: false,
      deletingAccount: false,
      canShowCompletionSteps: false,
      doNotShowEmailInfoDuringThisSession: false,
      formIsValid: false,
      disableUserCompletionStepsModal: false,
      subscribedToNewsletter: false,
    };
  },

  computed: {
    verified(): boolean {
      return store.state.accountVerified;
    },

    subscriptionStatus(): string {
      return store.state.subscriptionStatus;
    },

    subscriptionStatusAllowsToUseTheApp(): boolean {
      return (
        store.state.subscriptionStatus === 'free' ||
        store.state.subscriptionStatus === 'trial' ||
        store.state.subscriptionStatus === 'paid'
      );
    },

    lackOfFullAccess(): boolean {
      return !this.subscriptionStatusAllowsToUseTheApp || !this.verified;
      // || !this.onboardedFully;
    },

    password(): string {
      return this.clientProfilePersonalDetails.password;
    },
  },

  watch: {
    password(): void {
      if (!this.password) return;

      passwordStrength.check(this, '#clientProfilePassword');
    },
  },

  beforeCreate(): void {
    store.dispatch('meta/handleHeavyPageVisit');
  },

  async mounted(): Promise<void> {
    this.runLogicForNoValidSubscriptionButAtLeastOneOngoingProject();
    await this.getClientData();

    this.canShowCompletionSteps = true;
  },

  methods: {
    async getClientData(): Promise<void> {
      this.processingUserDataRequest = true;

      const resp = await requestAPI({ url: '/api/v1/clients/me', method: 'GET' });

      if (resp.ok) {
        const data = await resp.json();
        this.userSubmittedOnboardingBasics = data.contact_details;
        this.originalEmail = this.clientProfilePersonalDetails.email = data.email;
        this.subscribedToNewsletter = data.newsletter;
        store.commit('setUserId', data.id);

        if (this.userSubmittedOnboardingBasics) {
          this.onboardedFully = data.onboarded_fully;
          this.clientProfilePersonalDetails.firstName = data.contact_details.first_name;
          this.clientProfilePersonalDetails.surname = data.contact_details.surname;
          this.clientProfilePersonalDetails.initials = data.contact_details.initials;
          this.clientProfilePersonalDetails.gender = data.contact_details.gender;
          this.clientProfilePersonalDetails.position = data.company_position;
          this.clientProfilePersonalDetails.password = data.password;
        }

        if (data.company) {
          this.assignCompanyData(data.company);
        }
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.processingUserDataRequest = false;
    },

    async updateDetails(): Promise<void> {
      formValidators.validateForm(this, 'clientProfilePersonalDetails');

      if (!this.formIsValid) return;

      this.processingUserDataRequest = true;

      const payload = {
        email: this.clientProfilePersonalDetails.email,
        password: this.clientProfilePersonalDetails.password,
        first_name: this.clientProfilePersonalDetails.firstName,
        surname: this.clientProfilePersonalDetails.surname,
        position: this.clientProfilePersonalDetails.position,
        initials: this.clientProfilePersonalDetails.initials,
        gender: this.clientProfilePersonalDetails.gender,
      };

      if (!this.clientProfilePersonalDetails.password) delete payload.password;

      const resp = await requestAPI({
        method: 'PATCH',
        contentType: 'application/merge-patch+json',
        url: '/api/v1/clients/me',
        payload,
      });

      if (resp.ok) {
        this.$message.success(this.$t('yourInformationHasBeenUpdated'));

        const emailChanged = this.clientProfilePersonalDetails.email !== this.originalEmail;

        if (emailChanged) {
          window.location.reload();
        } else {
          const makeImmediateRefreshTokenCall = true;
          const clearOldRefrTokenTimeout = true;
          refreshTokenHandler(makeImmediateRefreshTokenCall, clearOldRefrTokenTimeout);
        }
      } else if (resp.status === 400 && payload?.password) {
        const data = await resp.json();

        passwordStrength.notifyAboutWeakPass(this, data.detail);
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.processingUserDataRequest = false;
    },

    async updateCompany(): Promise<void> {
      formValidators.validateForm(this, 'clientProfileCompanyDetails');

      if (!this.formIsValid) return;

      this.processingUserDataRequest = true;

      const payload = {
        emails: [this.clientProfileCompanyDetails.companyEmail],
        name: this.clientProfileCompanyDetails.companyName,
        addresses: [
          {
            street: this.clientProfileCompanyDetails.street,
            house_number: this.clientProfileCompanyDetails.houseNumber,
            apartment_number: this.clientProfileCompanyDetails.apartmentNumber,
            city: this.clientProfileCompanyDetails.city,
            postal_code: this.clientProfileCompanyDetails.postalCode,
            country: this.clientProfileCompanyDetails.country,
          },
        ],
        vat: this.clientProfileCompanyDetails.vatNumber,
        registration_number: this.clientProfileCompanyDetails.registrationNumber,
        phones: [this.clientProfileCompanyDetails.phone],
        uses_po: this.clientProfileCompanyDetails.PONumbers,
      };

      let resp: Response;

      if (this.companyId) {
        resp = await requestAPI({
          method: 'PATCH',
          contentType: 'application/merge-patch+json',
          url: `/api/v1/companies/${this.companyId}`,
          payload,
        });
      } else {
        resp = await requestAPI({
          method: 'POST',
          url: `/api/v1/companies`,
          payload,
        });
      }

      if (resp.ok) {
        this.$message.success(this.$t('yourInformationHasBeenUpdated'));

        const makeImmediateRefreshTokenCall = true;
        const clearOldRefrTokenTimeout = true;
        refreshTokenHandler(makeImmediateRefreshTokenCall, clearOldRefrTokenTimeout);
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.processingUserDataRequest = false;
    },

    assignCompanyData(companyData: {
      name: string;
      vat: string | string[];
      registration_number: string;
      phones: string[];
      emails: string[];
      addresses: {
        country: string;
        street: string;
        apartment_number: string;
        house_number: string;
        city: string;
        postal_code: string;
      }[];
      id: string;
      uses_po: boolean;
    }): void {
      this.clientProfileCompanyDetails.companyEmail = companyData.emails[0];
      this.clientProfileCompanyDetails.companyName = companyData.name;
      this.clientProfileCompanyDetails.street = companyData.addresses[0].street;
      this.clientProfileCompanyDetails.houseNumber = companyData.addresses[0].house_number;
      this.clientProfileCompanyDetails.apartmentNumber = companyData.addresses[0].apartment_number;
      this.clientProfileCompanyDetails.city = companyData.addresses[0].city;
      this.clientProfileCompanyDetails.postalCode = companyData.addresses[0].postal_code;
      this.clientProfileCompanyDetails.country = companyData.addresses[0].country;
      this.clientProfileCompanyDetails.vatNumber = companyData.vat;
      this.clientProfileCompanyDetails.registrationNumber = companyData.registration_number;
      this.clientProfileCompanyDetails.phone = companyData.phones[0];
      this.clientProfileCompanyDetails.PONumbers = companyData.uses_po;
      this.companyId = companyData.id;
    },

    showEmailChangeInfoModal(): void {
      if (this.doNotShowEmailInfoDuringThisSession) return;

      this.$notify.warning({
        title: this.$t('important'),
        message: this.$t('emailChangeWarning'),
        duration: 20000,
        offset: 120,
        onClose: this.doNotShowEmailInfoDuringThisSession = true,
      });
    },

    logOut(): void {
      authLogOut();
    },

    askIfSureToDelete(): void {
      helpToDeleteAccount(this, 'client');
    },

    async runLogicForNoValidSubscriptionButAtLeastOneOngoingProject(): Promise<void> {
      const ongoingProjectsNumber = await this.getOngoingProjectsNumber();
      const noSubscriptionPlan = !this.subscriptionStatusAllowsToUseTheApp;
      const noValidSubscriptionButAtLeastOneOngoingProject =
        ongoingProjectsNumber && noSubscriptionPlan;

      if (noValidSubscriptionButAtLeastOneOngoingProject) {
        store.commit('setNoSubscriptionButOngoingProjects', true);
        this.disableUserCompletionStepsModal = true;
        this.displayModalForNoValidSubscriptionButAtLeastOneOngoingProject(ongoingProjectsNumber);
      }
    },

    async getOngoingProjectsNumber(): Promise<number> {
      const response = await requestAPI({
        url: `/api/v1/assessment-requests/summary`,
        method: 'get',
      });

      if (response.ok) {
        const data = await response.json();
        const ongoing = data.find((projects: { status: string }) => {
          return projects.status === 'confirmed';
        });
        const number = ongoing?.count;
        return number;
      } else if (response.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
        return 0;
      }
    },

    displayModalForNoValidSubscriptionButAtLeastOneOngoingProject(
      ongoingProjectsNumber: number,
    ): void {
      this.$confirm(
        this.$t('noSubStillProjectsModalText', { ongoingProjectsNumber }),
        this.$t('noSubStillProjectsModalTitle'),
        {
          confirmButtonText: this.$t('goToReports'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning',
        },
      )

        .then(async () => {
          store.commit('applyAssessmentsFilter', { filter: 'todo', mode: 'reports' });
          router.push({ path: '/events-list', query: { mode: 'reports' } });
        })

        .catch((err: Error) => err);
    },

    async submitNewsletterSubscription(): Promise<void> {
      const userId = store.state.userId;
      const subscribe = this.subscribedToNewsletter;

      const resp = await newsletter.updateSubscription(userId, subscribe);

      if (resp.ok) {
        this.$message.success(this.$t('yourSubscriptionStatusHasBeenUpdated'));
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }
    },
  },
};
